// Leaflet helpers ported from the original theme's strava-functions.js.
// These are framework-agnostic; React components own the map lifecycle and call
// into these helpers.
import L from './leaflet-setup.js';
import { distanceMiles, pacePerMile, elevationFeet, stravaActivityUrl } from './format.js';

const STRAVA_ORANGE = '#fc5200';

/** Create a Leaflet map on the given container with the given options. */
export function createMap(container, options) {
  const map = L.map(container, options).setView([40.68053, -111.778445], 11);
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);
  return map;
}

/** Map option presets for the different map types used on the site. */
export function mapOptionSelector(mapType) {
  if (mapType === 'randomDraw' || mapType === 'recentDisplay') {
    return {
      zoomControl: false,
      doubleClickZoom: false,
      closePopupOnClick: false,
      dragging: false,
      zoomSnap: false,
      zoomDelta: false,
      trackResize: false,
      touchZoom: false,
      scrollWheelZoom: false,
      attributionControl: false,
    };
  }
  if (mapType === 'allRuns') {
    return { scrollWheelZoom: false, attributionControl: false };
  }
  return {};
}

/** Remove every polyline layer from a map (keeps the tile layer). */
export function clearMap(map) {
  for (const i in map._layers) {
    if (map._layers[i]._path !== undefined) {
      try {
        map.removeLayer(map._layers[i]);
      } catch (e) {
        console.log('problem with ' + e + map._layers[i]);
      }
    }
  }
}

/**
 * Adds every activity polyline to a map, with a popup + click-to-highlight.
 * Highlight state is scoped per call (per map) via a closure.
 */
export function addAllDataToMap(map, data) {
  let highlight = null;
  const removeHighlight = () => {
    if (highlight !== null) {
      highlight.setStyle({ color: STRAVA_ORANGE, weight: 3 });
      highlight.bringToBack();
      highlight = null;
    }
  };

  for (let x = 0; x < data.length; x++) {
    if (!data[x].map || !data[x].map.summary_polyline) continue;
    const coords = L.Polyline.fromEncoded(data[x].map.summary_polyline).getLatLngs();
    const line = L.polyline(coords, {
      color: STRAVA_ORANGE,
      weight: 3,
      opacity: 1,
      lineJoin: 'round',
    });
    line.bindPopup(
      '<a class="map-popup-text" href="' +
        stravaActivityUrl(data[x]) +
        '" target="_blank">' +
        data[x].name +
        '<br>' +
        distanceMiles(data[x]) +
        ' mi <br>' +
        pacePerMile(data[x]) +
        ' / mi <br>' +
        elevationFeet(data[x]) +
        ' ft </a>',
    );
    line.on('click', () => {
      removeHighlight();
      line.setStyle({ color: 'yellow', weight: 5 });
      line.bringToFront();
      highlight = line;
    });
    line.addTo(map);
  }

  map.on('click', removeHighlight);
  return removeHighlight;
}

/** Add a single activity's polyline to a map, optionally fitting bounds. */
export function addSingleLineToMap(map, activity, paddingValue, color, weight, opacity) {
  const coords = L.Polyline.fromEncoded(activity.map.summary_polyline).getLatLngs();
  if (paddingValue !== 0) {
    map.fitBounds(L.latLngBounds(coords), { padding: [paddingValue, paddingValue] });
  }
  L.polyline(coords, { color, weight, opacity, lineJoin: 'round' }).addTo(map);
}

/**
 * Animated "snake draw" of a single random run. When the animation finishes it
 * invokes `onComplete` so the caller can pick the next run.
 */
export function randomMapDraw(map, activity, onComplete) {
  clearMap(map);

  const coords = L.Polyline.fromEncoded(activity.map.summary_polyline).getLatLngs();
  const line = L.polyline(coords, {
    snakingSpeed: 300,
    color: '#ffccb3',
    weight: 5,
    opacity: 1,
    lineJoin: 'round',
  });
  const line2 = L.polyline(coords, {
    snakingSpeed: 300,
    color: STRAVA_ORANGE,
    weight: 3,
    opacity: 0.8,
    lineJoin: 'round',
  });

  map.flyToBounds(L.latLngBounds(coords), { padding: [300, 100], animate: true, duration: 4 });
  const flyTimeout = setTimeout(() => {
    line2.addTo(map).snakeIn();
    line.addTo(map).snakeIn();
    const outTimeout = setTimeout(() => line.snakeOut(), 300);
    line.on('snakeOutEnd', () => {
      clearTimeout(outTimeout);
      onComplete();
    });
  }, 4200);

  // Return a canceller so React can clean up if the component unmounts mid-draw.
  return () => clearTimeout(flyTimeout);
}

/** Pick a new random index that differs from the previous one. */
export function getNewMapID(dataLen, prevMapID) {
  let newMapID = Math.floor(Math.random() * dataLen);
  while (newMapID === prevMapID) {
    newMapID = Math.floor(Math.random() * dataLen);
  }
  return newMapID;
}

/*
 * Date-filter logic for the all-runs map, ported verbatim from page-run-map.php.
 * A "time frame" is a "year.month.day.hour" string where -1 means "any".
 */

function onTimeFrame(y, m, d, h, ys, ms, ds, hs, ye, me, de, he, duration) {
  if (
    ((ys <= y && y <= ye) || y === -1) &&
    ((ms <= m && m <= me) || m === -1) &&
    ((ds <= d && d <= de) || d === -1) &&
    ((hs <= h && h <= he) || h === -1 || duration >= 86400)
  ) {
    return true;
  }
  if (
    ((ys <= y && y <= ye) || y === -1) &&
    ((ms <= m && m <= me) || m === -1) &&
    ((ds <= d && d <= de) || d === -1) &&
    he < hs
  ) {
    if (h < he || h > hs) {
      return true;
    }
  }
  return false;
}

function inTimeFrame(startTime, duration, timeFrame) {
  const ys = parseInt(startTime.split('-')[0]);
  const ms = parseInt(startTime.split('-')[1]);
  const ds = parseInt(startTime.split('-')[2].split('T')[0]);
  const hs = parseInt(startTime.split('T')[1].split(':')[0]);

  const dateTransfer = new Date(ys, ms, ds, hs, 0, duration);

  const ye = dateTransfer.getFullYear();
  const me = dateTransfer.getMonth();
  const de = dateTransfer.getDate();
  const he = dateTransfer.getHours();

  const tfSplit = timeFrame.split('.');
  const y = parseInt(tfSplit[0]);
  const m = parseInt(tfSplit[1]);
  const d = parseInt(tfSplit[2]);
  const h = parseInt(tfSplit[3]);

  return onTimeFrame(y, m, d, h, ys, ms, ds, hs, ye, me, de, he, duration);
}

/** Filter activities down to those that fall within a "y.m.d.h" time frame. */
export function filterDataTimeFrame(data, timeFrame) {
  if (timeFrame === '' || timeFrame === '-1.-1.-1.-1') return data;
  return data.filter((a) => inTimeFrame(a.start_date_local, a.elapsed_time, timeFrame));
}
